// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  type BaseFeatureAnalysis,
  type PortfolioAnalysis,
  type RiskReturns as RiskReturnsAnalysis,
  type StockAnalysis,
} from '@shared/data-access/interfaces/api/ai/portfolio-analysis';
import {
  CountryChart,
  DonutChartContainer,
  SectorComposition,
  SegmentChart,
  ThermometerGraph,
} from '@shared/ui-kit/components';
import { KeyMetricsTable } from '@shared/ui-kit/features/overview';
import { useTranslation } from 'react-i18next';
const SECTOR_KEY_MAP: Record<string, string> = {
  'Information Technology': 'information-technology',
  Financials: 'financials',
  'Consumer Discretionary': 'consumer-discretionary',
  Industrials: 'industrials',
  'Health Care': 'health-care',
  'Communication Services': 'communication-services',
  'Consumer Staples': 'consumer-staples',
  Energy: 'energy',
  'Real Estate': 'real-estate',
  Utilities: 'utilities',
  Materials: 'materials',
  Other: 'other',
};
interface OverviewTemplateProps {
  analysis: PortfolioAnalysis;
}

export const OverviewTemplate = ({ analysis }: OverviewTemplateProps) => {
  const { t } = useTranslation();

  const portfolioAnalysis: PortfolioAnalysis | any = analysis;

  const categoryName: string[] =
    portfolioAnalysis.asset_categories?.map(
      (category: BaseFeatureAnalysis) => category.exposure,
    ) || [];

  const categoryPercentage: number[] =
    portfolioAnalysis.asset_categories?.map(
      (category: BaseFeatureAnalysis) => category.weight,
    ) || [];

  const currencyName = portfolioAnalysis.currencies.map(
    (category: BaseFeatureAnalysis) => category.exposure,
  );

  const currencyWeight = portfolioAnalysis.currencies.map(
    (category: BaseFeatureAnalysis) => category.weight,
  );

  const assetName = portfolioAnalysis.fund_segments.map(
    (segment: BaseFeatureAnalysis) => segment.exposure,
  );

  const assetPercentage = portfolioAnalysis.fund_segments.map(
    (segment: BaseFeatureAnalysis) => segment.weight * 100,
  );

  const sectorsName = portfolioAnalysis.fund_sectors.map(
    (segment: BaseFeatureAnalysis) =>
      t(`common.sector.${SECTOR_KEY_MAP[segment.exposure] ?? 'other'}`, {
        ns: 'common',
      }),
  );
  const sectorsPercentage = portfolioAnalysis.fund_sectors.map(
    (segment: BaseFeatureAnalysis) => segment.weight,
  );
  const countryName = portfolioAnalysis.fund_countries.map(
    (segment: BaseFeatureAnalysis) => segment.exposure,
  );
  const countryPercentage = portfolioAnalysis.fund_countries.map(
    (segment: BaseFeatureAnalysis) => segment.weight,
  );

  const riskReturnPortfolio = portfolioAnalysis?.risk_returns?.find(
    (rr: RiskReturnsAnalysis) => rr.ticker === 'Portfolio',
  );
  const thermometerReturn = riskReturnPortfolio?.returns || 0;

  const thermometerVolatility = riskReturnPortfolio?.volatility || 0;

  const thermometerSharpe = portfolioAnalysis.risk?.sharpe || 0;

  return (
    <>
      <div className="grid grid-cols-4 gap-x-6">
        <ThermometerGraph type="return" value={thermometerReturn} />
        <ThermometerGraph type="volatility" value={thermometerVolatility} />
        <ThermometerGraph type="sharpe" value={thermometerSharpe} />
        <ThermometerGraph
          type="expense"
          value={portfolioAnalysis?.fund_expenses.portfolio_expenses as number}
        />
      </div>
      <div className="risk-return-grid">
        <KeyMetricsTable analysis={analysis as StockAnalysis} />
        <SectorComposition
          columns={{ portfolio: true }}
          categories={sectorsName}
          percentages={sectorsPercentage}
        />
        <CountryChart countries={countryName} percentages={countryPercentage} />
        <DonutChartContainer
          name={t('common.currency', { ns: 'common' })}
          exposure={currencyName}
          weight={currencyWeight}
        />
        {categoryName.length > 0 && (
          <DonutChartContainer
            name={t('glossary.asset-class', { ns: 'glossary' })}
            exposure={categoryName}
            weight={categoryPercentage}
          />
        )}
        <SegmentChart categories={assetName} percentage={assetPercentage} />
      </div>
    </>
  );
};
